
import React, { useState, useEffect, useRef } from 'react';
import RobotComponent from '../../components/Robot/RobotComponent';
import { Language, Dialect, AgentProfile, User } from '../../types';
import { generateResponse, generateAudio } from '../../services/geminiService';
import { audioService } from '../../services/audioService';
import { AgentsService } from '../../api';
import { useDataTemplate } from '../../hooks/useDataTemplate';

interface RobotTemplateProps {
  language: Language;
  dialect: Dialect;
  voice: string;
  speed: number;
  user: User;
  onBack: () => void;
  agents: AgentProfile[]; // Deprecated props
  setAgents: React.Dispatch<React.SetStateAction<AgentProfile[]>>; // Deprecated props
}

type RobotState = 'idle' | 'listening' | 'processing' | 'speaking';

const RobotTemplate: React.FC<RobotTemplateProps> = (props) => {
  const { language, user } = props;
  const isRtl = language === 'ar';
  
  // --- Data Gateway ---
  const { data: agents, setData: setAgents, isLoading } = useDataTemplate<AgentProfile[]>(AgentsService.getAll);

  const [activeAgentId, setActiveAgentId] = useState<string>('');
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [state, setState] = useState<RobotState>('idle');
  const [activeTab, setActiveTab] = useState<'agents'|'visual'|'options'>('visual');
  const recognitionRef = useRef<any>(null);

  // Sync state to local storage for local mode
  useEffect(() => {
    if (agents) localStorage.setItem('lahja_agents', JSON.stringify(agents));
  }, [agents]);

  // Initial Agent Selection
  useEffect(() => {
      if (agents && agents.length > 0 && (!activeAgentId || !agents.find(a => a.id === activeAgentId))) {
          setActiveAgentId(agents[0].id);
      }
  }, [agents, activeAgentId]);

  const activeAgent = agents?.find(a => a.id === activeAgentId) || (agents && agents[0]);

  const hasApiAccess = user.plan === 'pro' || user.plan === 'enterprise';
  const agentLimit = user.plan === 'enterprise' ? 20 : user.plan === 'pro' ? 10 : 2;

  // Actions
  const handleAddAgent = async () => {
    if (!agents) return;
    if (agents.length >= agentLimit) {
       alert(isRtl ? `الحد الأقصى هو ${agentLimit}` : `Limit of ${agentLimit} reached`);
       return;
    }
    const newId = Date.now().toString();
    const newAgent: AgentProfile = {
      id: newId,
      name: isRtl ? 'وكيل جديد' : 'New Agent',
      role: isRtl ? 'غير محدد' : 'Undefined',
      avatar: 'fa-user-astronaut',
      dialect: 'najdi',
      voice: 'Kore',
      systemPrompt: '',
      autoReply: true,
      voiceOutput: true,
      sensitivity: 0.5,
      stats: { interactions: 0, lastActive: '-' },
      apiKey: hasApiAccess ? `lahja_live_${newId}_${Math.random().toString(36).substring(7)}` : undefined,
      endpointUrl: hasApiAccess ? `https://api.lahja.app/v1/agents/${newId}/chat` : undefined
    };
    
    await AgentsService.create(newAgent);
    setAgents(prev => [...(prev || []), newAgent]);
    setActiveAgentId(newId);
    setActiveTab('options');
  };

  const handleUpdateAgent = async (updates: Partial<AgentProfile>) => {
    if (!activeAgent) return;
    const updatedAgent = { ...activeAgent, ...updates };
    setAgents(prev => prev?.map(a => a.id === activeAgentId ? updatedAgent : a) || []);
    // Debounce save in a real app, direct calling here
    await AgentsService.update(updatedAgent);
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;
    await AgentsService.delete(agentToDelete);
    const newAgents = agents?.filter(a => a.id !== agentToDelete) || [];
    setAgents(newAgents);
    if (activeAgentId === agentToDelete) setActiveAgentId(newAgents[0]?.id || '');
    setAgentToDelete(null);
  };

  const handleDuplicateAgent = async (agent: AgentProfile) => {
    if (!agents || agents.length >= agentLimit) return;
    const newId = Date.now().toString();
    const copy: AgentProfile = { ...agent, id: newId, name: `${agent.name} (Copy)`, apiKey: hasApiAccess ? `lahja_live_${newId}` : undefined };
    await AgentsService.create(copy);
    setAgents(prev => [...(prev || []), copy]);
  };

  // Speech Recognition Logic (Client Side Feature)
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false; 
      recognition.lang = language === 'ar' ? 'ar-SA' : 'en-US';
      recognition.interimResults = false;
      recognition.onstart = () => { if (isActive) setState('listening'); };
      recognition.onend = () => { if (isActive && state === 'listening') recognition.start(); };
      recognition.onresult = async (event: any) => {
        const transcript = event.results[0][0].transcript;
        if (transcript) processInput(transcript);
      };
      recognitionRef.current = recognition;
    }
  }, [language, isActive, state]);

  useEffect(() => {
    if (isActive && state === 'idle') {
       try { recognitionRef.current?.start(); setState('listening'); } catch(e) {}
    } else if (!isActive) {
       recognitionRef.current?.stop();
       setState('idle');
    }
  }, [isActive, state]);

  const processInput = async (input: string) => {
    if (!activeAgent) return;
    recognitionRef.current?.stop(); 
    setState('processing');
    
    if (!activeAgent.autoReply) {
       setState('idle'); return;
    }

    try {
      const history = [{ role: 'user', content: input }];
      const fullPrompt = activeAgent.systemPrompt 
        ? `[System: You are "${activeAgent.name}", Role: "${activeAgent.role}". Dialect instruction: ${activeAgent.systemPrompt}] User: ${input}`
        : input;

      const attachments = activeAgent.trainingFiles || [];

      // Use the centralized Gemini Service (which uses API key from config/env)
      const responseText = await generateResponse(fullPrompt, activeAgent.dialect, history, attachments, language);

      if (activeAgent.voiceOutput) {
         setState('speaking');
         const audioBase64 = await generateAudio(responseText, activeAgent.voice);
         if (audioBase64) await audioService.playPCM(audioBase64, props.speed);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setState('idle');
    }
  };

  if (isLoading) return <div className="flex h-full items-center justify-center bg-[#050507] text-white"><i className="fas fa-robot fa-spin text-3xl"></i></div>;

  return (
    <RobotComponent
      language={language}
      activeAgent={activeAgent}
      agents={agents || []}
      isActive={isActive}
      state={state}
      activeTab={activeTab}
      agentLimit={agentLimit}
      agentToDelete={agentToDelete}
      hasApiAccess={hasApiAccess}
      onBack={props.onBack}
      onSetActiveAgent={setActiveAgentId}
      onSetActiveTab={setActiveTab}
      onToggleRobot={() => setIsActive(!isActive)}
      onAddAgent={handleAddAgent}
      onDeleteAgent={setAgentToDelete}
      onConfirmDelete={confirmDeleteAgent}
      onCancelDelete={() => setAgentToDelete(null)}
      onDuplicateAgent={handleDuplicateAgent}
      onResetAgent={(id) => {}}
      onUpdateAgent={handleUpdateAgent}
      onCopyApiKey={() => activeAgent?.apiKey && navigator.clipboard.writeText(activeAgent.apiKey)}
    />
  );
};

export default RobotTemplate;
